import { ShowToastError } from "@platformx/utilities";
import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import slugApi from "../../services/slug/slug.api";

const previewSlug = (value: string): string => value.trim().normalize("NFC");

/** Normalises for the "is this the document's own current value" comparison. */
const normalizeForCompare = (value: string): string =>
  (value || "").trim().normalize("NFC").toLowerCase();

export type SlugValidationStatus =
  | "idle" // nothing checked yet / result invalidated by an edit
  | "empty" // empty or whitespace-only input — no API call made
  | "checking" // debounce window open or request in flight
  | "unique" // API confirmed the slug is unique for this docType
  | "not_unique" // API reported a collision for this docType
  | "error"; // API failed/timed out — non-blocking, server re-validates at submit

/** Debounce for blur-triggered checks; within the ticket's 300–500ms window. */
const DEBOUNCE_MS = 400;

/**
 * Slug-uniqueness validation state machine (KIWI-5865).
 *
 */
const useSlugValidation = (docType: string) => {
  const { t } = useTranslation();
  const [status, setStatus] = useState<SlugValidationStatus>("idle");
  const [isSubmitCheck, setIsSubmitCheck] = useState(false);
  const [slug, setSlug] = useState("");
  const debounceRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const abortRef = useRef<AbortController | null>(null);
  const requestIdRef = useRef(0);

  const cancelPending = useCallback(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    abortRef.current?.abort();
    requestIdRef.current += 1;
  }, []);

  const runCheck = useCallback(
    async (value: string, effectiveDocType: string, effectiveSchemaDocumentType?: string) => {
      const requestId = ++requestIdRef.current;
      const controller = new AbortController();
      abortRef.current = controller;
      try {
        const response = await slugApi.validateSlug(
          {
            slug: value,
            docType: effectiveDocType,
            schema_document_type: effectiveSchemaDocumentType,
          },
          { signal: controller.signal },
        );
        if (requestId !== requestIdRef.current) return; // superseded — ignore
        setSlug(previewSlug(value));
        setIsSubmitCheck(false);
        setStatus(response.isUnique ? "unique" : "not_unique");
      } catch (err: any) {
        if (requestId !== requestIdRef.current || controller.signal.aborted) return;
        setIsSubmitCheck(false);
        setStatus("error"); // non-blocking; submit-time server validation is the fallback
        const statusCode = err?.networkError?.statusCode || 0;
        ShowToastError(t("api_error_toast"), statusCode);
      }
    },
    [t],
  );

  /**
   * Blur-triggered, debounced check. Pass `excludeValue` (the document's own
   * currently-stored value) so re-confirming an unchanged value doesn't
   * collide against itself — the check API has no notion of "this document,"
   * only value + docType — and `docType` to scope this specific call to the
   * content's real stored category, overriding the hook's default.
   */
  const validate = useCallback(
    (
      value: string,
      options?: { excludeValue?: string; docType?: string; schema_document_type?: string },
    ) => {
      cancelPending();
      const trimmed = value?.trim() || "";
      if (!trimmed) {
        setSlug("");
        setIsSubmitCheck(false);
        setStatus("empty");
        return;
      }
      if (
        options?.excludeValue &&
        normalizeForCompare(trimmed) === normalizeForCompare(options.excludeValue)
      ) {
        // Unchanged from the document's own stored value — nothing was actually
        // checked, so don't report a result (callers use this to suppress any
        // "available"/"already exists" toast for a no-op edit).
        setSlug(previewSlug(trimmed));
        setIsSubmitCheck(false);
        setStatus("idle");
        return;
      }
      setIsSubmitCheck(false);
      setStatus("checking");
      const effectiveDocType = options?.docType ?? docType;
      debounceRef.current = setTimeout(() => {
        runCheck(trimmed, effectiveDocType, options?.schema_document_type);
      }, DEBOUNCE_MS);
    },
    [cancelPending, runCheck, docType],
  );

  /** Submit-time re-check (race-condition guard). No debounce. Same self-exclusion as `validate`. */
  const checkNow = useCallback(
    async (
      value: string,
      options?: { excludeValue?: string; docType?: string; schema_document_type?: string },
    ): Promise<boolean> => {
      cancelPending();
      const trimmed = value?.trim() || "";
      if (!trimmed) {
        setSlug("");
        setIsSubmitCheck(true);
        setStatus("empty");
        return false;
      }
      if (
        options?.excludeValue &&
        normalizeForCompare(trimmed) === normalizeForCompare(options.excludeValue)
      ) {
        setSlug(previewSlug(trimmed));
        setIsSubmitCheck(true);
        setStatus("unique");
        return true;
      }
      const requestId = ++requestIdRef.current;
      const controller = new AbortController();
      abortRef.current = controller;
      setIsSubmitCheck(true);
      setStatus("checking");
      try {
        const response = await slugApi.validateSlug(
          {
            slug: trimmed,
            docType: options?.docType ?? docType,
            schema_document_type: options?.schema_document_type,
          },
          { signal: controller.signal },
        );
        if (requestId === requestIdRef.current) {
          setSlug(previewSlug(trimmed));
          setIsSubmitCheck(true);
          setStatus(response.isUnique ? "unique" : "not_unique");
        }
        return response.isUnique;
      } catch (err: any) {
        if (requestId === requestIdRef.current && !controller.signal.aborted) {
          setIsSubmitCheck(true);
          setStatus("error");
          const statusCode = err?.networkError?.statusCode || 0;
          ShowToastError(t("api_error_toast"), statusCode);
        }
        return true; // fall back to server-side validation inside the create/update API
      }
    },
    [cancelPending, docType, t],
  );

  /** Editor changed the input after a check — discard the stale result. */
  const invalidate = useCallback(() => {
    cancelPending();
    setIsSubmitCheck(false);
    setStatus("idle");
  }, [cancelPending]);

  /**
   * Create/update API reported a collision at submit time. Deliberately
   * leaves `isSubmitCheck` as-is: this always follows a `checkNow()` call in
   * the same submit attempt, so it should stay attributed to that submit
   * rather than be treated as a fresh blur-triggered result.
   */
  const markNotUnique = useCallback(() => {
    cancelPending();
    setStatus("not_unique");
  }, [cancelPending]);

  const reset = useCallback(() => {
    cancelPending();
    setSlug("");
    setIsSubmitCheck(false);
    setStatus("idle");
  }, [cancelPending]);

  // Abort anything still pending on unmount.
  useEffect(() => cancelPending, [cancelPending]);

  const isBlocking = status === "checking" || status === "not_unique";

  return {
    status,
    slug,
    validate,
    checkNow,
    invalidate,
    markNotUnique,
    reset,
    isBlocking,
    isSubmitCheck,
  };
};

export default useSlugValidation;
